// As Oasis SW lifecycle hook (S7 Phase 5).
//
// The vite-plugin-pwa "prompt" strategy emits a `virtual:pwa-register`
// module that returns a `registerSW({ onNeedRefresh })` factory. We
// use the lower-level workbox-window directly so:
//   1. Tests don't need the virtual module mock.
//   2. The toast UI stays in our codebase and is testable.
//
// The hook returns a function the page can call to apply the pending
// SW (sends `SKIP_WAITING`, then reloads on `controllerchange`).

import { Workbox } from 'workbox-window';
import { useToastStore } from '../stores/toast.store';

export interface PwaUpdateHandle {
  applyUpdate: () => void;
}

export function registerServiceWorker(): PwaUpdateHandle | null {
  if (!('serviceWorker' in navigator)) return null;
  // Skip in dev (vite-plugin-pwa is configured devOptions.enabled=false).
  if (import.meta.env.DEV) return null;

  const wb = new Workbox('/sw.js', { scope: '/' });

  function applyUpdate() {
    wb.addEventListener('controlling', () => {
      window.location.reload();
    });
    void wb.messageSkipWaiting();
  }

  wb.addEventListener('waiting', () => {
    useToastStore.getState().push({
      message: 'New version ready',
      durationMs: 0,
      action: { label: 'Reload', onClick: applyUpdate },
    });
  });

  // Optional: surface activation failures so we don't fail silently.
  wb.addEventListener('redundant', () => {
    // The SW was redundant before activating — don't toast (noisy).
    // Lifecycle quirks here are common during local dev rebuilds.
  });

  void wb.register();

  return { applyUpdate };
}
